import { canNavigate, ConductorOneChildActive, Router, ScreenBase, splitUrlSegments } from "@frui.ts/screens";
import LocalizationService from "services/localizationService";
import SingleBankTransferViewModel, { SingleBankTransferNavigationName } from "../common/payment/singleBankTransferViewModel";
import SingleStatusViewModel, { SingleStatusViewNavigationName } from "../common/payment/singleStatusViewModel";
import StatusViewModel, { StatusViewNavigationName } from "../common/payment/statusViewModel";
import NotificationService, { NotificationType } from "services/notificationService";
import { IconType } from "@edaz/edazkit/Icon";
import { action, computed, reaction, when } from "mobx";
import { clearSearchParameters } from "utils/browserHistory";
import { isSafari } from "react-device-detect";
import OutageService from "services/outageService";
import OrderOutageViewModel from "../common/orderOutage/orderOutageViewModel";
import OrderWizardViewModel, { OrderNewViewNavigationName } from "./order/orderWizardModel";
import ChargeOrder, { ChargeOrderStatus } from "models/chargeOrder";
import LicenseViewModel, { LicenseViewNavigationName } from "./order/steps/licenseViewModel";
import { BankTransferNavigationName } from "viewModels/common/payment/bankTransferViewModel";
import TimeService from "services/timeService";
import ChargeOrderRepository from "repositories/chargeOrderRepository";
import EventBus, { currentServerTime } from "services/eventBus";

export const EshopModuleNavigationName = "eshop";

@Router.registerRoute({
  route: EshopModuleNavigationName,
  name: Router.Self,
  children: [OrderWizardViewModel, SingleBankTransferViewModel, SingleStatusViewModel, OrderOutageViewModel],
})
export default class EshopViewModel extends ConductorOneChildActive<ScreenBase> {
  private unsubsriptions: Array<() => void> = [];
  OFFLINE_NOTIFICATION_KEY = "OFFLINE";

  @computed
  get outage() {
    return this.outageService.eshopActiveOutage;
  }

  constructor(
    public localizationService: LocalizationService,
    private orderWizardModel: OrderWizardViewModel,
    public timeService: TimeService,
    private eventBus: EventBus,
    private outageService: OutageService,
    public repository: ChargeOrderRepository,
    private bankTransferVMFactory: ReturnType<typeof SingleBankTransferViewModel.Factory>,
    private statusVMFactory: ReturnType<typeof SingleStatusViewModel.Factory>,
    private orderOutageViewModelFactory: ReturnType<typeof OrderOutageViewModel.Factory>,
    private notificationService: NotificationService
  ) {
    super();
    this.navigationName = EshopModuleNavigationName;
    this.nameValue = "eshop.title";
    this.children.push(this.orderWizardModel);
    if (history.scrollRestoration) {
      history.scrollRestoration = "manual";
    }

    if (isSafari) {
      window.onpageshow = function (event: { persisted: boolean }) {
        if (event.persisted) {
          window.location.reload();
        }
      };
    }
  }

  async onActivate() {
    window.addEventListener("online", this.hideOfflineNotification);
    window.addEventListener("offline", this.showOfflineNotification);
    const serverTime = await this.timeService.getCurrentServerTime(true);
    this.eventBus.publish(currentServerTime({ date: serverTime }));
    this.unsubsriptions.push(reaction(() => this.outage, this.onEshopOutageChanged));
    this.lazyCheckEshopOutage();

    if (!this.activeChild) {
      await this.tryActivateChild(this.orderWizardModel);
    }

    await super.onActivate();
  }

  protected async lazyCheckEshopOutage() {
    await when(() => this.outageService.isInitialized === true);
    if (this.outage && this.activeChild === this.orderWizardModel) {
      this.tryActivateChild(this.orderOutageViewModelFactory(this.outage));
    }
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    window.removeEventListener("online", this.hideOfflineNotification);
    window.removeEventListener("offline", this.showOfflineNotification);
    return super.onDeactivate(isClosing);
  }

  @action.bound
  private onEshopOutageChanged() {
    if (this.outage === undefined) {
      this.tryActivateChild(this.orderWizardModel);
    }
  }

  @action.bound
  protected async changeActiveChild(newChild: ScreenBase | undefined, closePrevious: boolean): Promise<any> {
    // UC - Safari payment with card without 3DS - user click back button - need to reset order
    if (this.activeChild instanceof StatusViewModel && newChild instanceof OrderWizardViewModel) {
      this.orderWizardModel.resetOrder();
    }
    super.changeActiveChild(newChild, closePrevious);
  }

  @action.bound
  private hideOfflineNotification() {
    this.notificationService.remove(this.OFFLINE_NOTIFICATION_KEY);
  }

  @action.bound
  private showOfflineNotification() {
    this.notificationService.add(
      {
        title: this.localizationService.translateGeneral("notifications.offline"),
        type: NotificationType.ERROR,
        icon: IconType.Offline,
        permanent: true,
      },
      this.OFFLINE_NOTIFICATION_KEY
    );
  }

  // Base method overload because BankTransfer needs 2 parts or url
  // tslint:disable-next-line:cognitive-complexity
  async navigate(subPath: string | undefined, params: any) {
    const segments = splitUrlSegments(subPath);
    let childToActivate;
    if (segments.length > 0 && (segments[0] === SingleBankTransferNavigationName || segments[0] === BankTransferNavigationName)) {
      childToActivate = await this.findNavigationChild(subPath || "");
    } else if (segments.length > 1 && segments[0] === SingleStatusViewNavigationName) {
      childToActivate = await this.findNavigationChild(subPath || "");
    } else if (segments.length > 1 && segments[0] === StatusViewNavigationName && segments[1]) {
      const order = this.orderWizardModel.chargeOrder;

      const result = params.result;
      const failureType = params["failure_type"];
      const failureOrigin = params["failure_origin"];

      if (order) {
        clearSearchParameters();
        await this.orderWizardModel.chargeOrderService.updateChargeOrderStatus(result, order, segments[1]);
      }

      this.orderWizardModel.paymentResultFailureType = failureType;
      this.orderWizardModel.paymentResultFailureOrigin = failureOrigin;

      childToActivate = await this.findNavigationChild(subPath || "");
    } else {
      childToActivate = await this.findNavigationChild(segments[0] || "");
    }
    if (
      segments[1] &&
      segments[1] !== LicenseViewNavigationName &&
      this.orderWizardModel.activeChild instanceof LicenseViewModel
    ) {
      const isValid = this.orderWizardModel.activeChild.validateAll();

      if (!isValid) return;
    }

    if (childToActivate) {
      await this.tryActivateChild(childToActivate);

      if (canNavigate(childToActivate)) {
        await childToActivate.navigate(segments[1], params);
      }
    }
    await this.onChildNavigated(childToActivate);
  }

  @action.bound
  async getOrderStatus(id: string) {
    return (await this.repository.get(id))?.status;
  }

  async findNavigationChild(navigationName: string) {
    if (navigationName.includes(SingleBankTransferNavigationName) || navigationName.includes(BankTransferNavigationName)) {
      const segments = splitUrlSegments(navigationName);
      if (segments.length > 1 && segments[1]) {
        const chargeOrder = this.orderWizardModel.chargeOrder;
        if (chargeOrder && chargeOrder._status === ChargeOrderStatus.PaidBankTransfer) {
          this.orderWizardModel.chargeOrderService.persistStoredChargeOrder(chargeOrder);
          return this.bankTransferVMFactory(chargeOrder);
        }
      }
    } else if (navigationName.includes(StatusViewNavigationName) || navigationName.includes(SingleStatusViewNavigationName)) {
      const segments = splitUrlSegments(navigationName);
      if (segments.length > 1 && segments[1]) {
        const chargeOrder = this.orderWizardModel.chargeOrder;
        const status = await this.getOrderStatus(this.orderWizardModel.chargeOrder.id);
        if (chargeOrder && status === ChargeOrderStatus.Paid) {
          chargeOrder._status = status;
          return this.statusVMFactory(chargeOrder);
        } else {
          return super.findNavigationChild(OrderNewViewNavigationName);
        }
      }
    } else if (navigationName === OrderNewViewNavigationName && this.outage) {
      return this.orderOutageViewModelFactory(this.outage);
    }
    return super.findNavigationChild(navigationName);
  }
}
